import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import '../css/QuestionGenerator.css';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  FaMagic,
  FaRedo,
  FaTrash,
  FaCheck,
  FaLightbulb,
  FaListUl,
} from "react-icons/fa";
import { toast } from 'react-toastify';
import { BlockMath, InlineMath } from "react-katex";

const CreateObjectiveQuestion = ({

  subjectId,
  subjectName,
  setInstObjs,
}) => {
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("easy");
  const [numQuestions, setNumQuestions] = useState(1);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false); // For generating
  const [saving, setSaving] = useState(false); // ✅ For saving

  // ✅ Token setup
  const tokenString = localStorage.getItem("authToken");
  let headers = { "Content-Type": "application/json" };
  if (tokenString) {
    try {
      const token = JSON.parse(tokenString);
      headers.Authorization = `Bearer ${token.access}`;
    } catch (e) {
      console.warn("Failed to parse authToken:", e);
    }
  }

  const generateQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/generate-objective/`,
        {
          subject: subjectName,
          topic,
          difficulty,
          num_questions: numQuestions,
        },
        { headers }
      );

      let generatedArray;

      if (Array.isArray(response.data.generated)) {
        generatedArray = response.data.generated;
      } else {
        generatedArray = response.data.generated
          .split(/\nQuestion:/g)                         // split correctly
          .map((block, index) => {
            if (index === 0) return block.trim();
            return "Question:" + block.trim();
          })
          .filter(q => q.trim().length > 0);
      }

      setResults(generatedArray);
    } catch (error) {
      console.error(error);
      toast.error("Error generating question.");
    }
    setLoading(false);
  };

  const removeQuestion = (index) => {
    setResults((prev) => prev.filter((_, i) => i !== index));
  };

  const saveQuestions = async () => {
    setSaving(true);
    try {
      const savedQuestions = [];

      for (const block of results) {
        const lines = block.split("\n").map((line) => line.trim());

        const questionLine = lines.find((l) => /^question\s*\d*:/i.test(l));
        const questionText = questionLine
          ? questionLine.replace(/^Question\s*\d*:\s*/i, "").trim()
          : null;

        if (!questionText) continue;

        const optionA =
          lines.find((l) => l.startsWith("A)"))?.replace(/^A\)\s*/i, "") || "";
        const optionB =
          lines.find((l) => l.startsWith("B)"))?.replace(/^B\)\s*/i, "") || "";
        const optionC =
          lines.find((l) => l.startsWith("C)"))?.replace(/^C\)\s*/i, "") || "";
        const optionD =
          lines.find((l) => l.startsWith("D)"))?.replace(/^D\)\s*/i, "") || "";

        let correctLine =
          lines.find((l) => /(\*\*)?(answer|correct):/i.test(l)) || "";
        let match = correctLine.match(/\b[A-D]\b/i);
        let correct = match ? match[0].toUpperCase() : "A";

        const payload = {
          question_text: questionText,
          option_a: optionA,
          option_b: optionB,
          option_c: optionC,
          option_d: optionD,
          correct_answer: correct,
          image_url: null,
        };

        const { data } = await axios.post(
          `${API_URL}/instAIobj/${subjectId}/`,
          payload,
          { headers }
        );

        savedQuestions.push(data); // store returned question object
      }

      // ✅ Append newly saved questions to local state
      setInstObjs((prev) => [...savedQuestions, ...prev]);

      toast.success("Questions saved successfully!");
      setResults([]);
    } catch (error) {
      console.error(error);
      toast.error("Error saving questions.");
    }
    setSaving(false);
  };

  const renderContent = (text) => {
    if (!text) return null;

    const trimmed = text.trim();

    // Detect $$ block math $$
    if (trimmed.startsWith("$$") && trimmed.endsWith("$$")) {
      const math = trimmed.replace(/^\$\$|\$\$$/g, "");
      return <BlockMath math={math} />;
    }

    // Detect $ inline math $
    if (trimmed.includes("$")) {
      const parts = trimmed.split(/(\$[^$]+\$)/g);

      return (
        <>
          {parts.map((part, index) => {
            if (part.startsWith("$") && part.endsWith("$")) {
              return (
                <InlineMath
                  key={index}
                  math={part.replace(/^\$|\$$/g, "")}
                />
              );
            } else {
              return <span key={index}>{part}</span>;
            }
          })}
        </>
      );
    }

    // Detect raw LaTeX (no $ included)
    const hasMathSymbols = /\\frac|\\sqrt|\\log|\\sin|\\cos|\\tan|\^|_/.test(trimmed);
    if (hasMathSymbols) {
      return <InlineMath math={trimmed} />;
    }

    // Default: plain text
    return <span>{text}</span>;
  };

  return (
    <Box>
      {/* ── Generate Form ───────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: 2,
          background: "rgba(124,58,237,0.04)",
          border: "1px solid rgba(124,58,237,0.14)",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#7c3aed",
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            mb: 2,
          }}
        >
          🤖 AI Question Generator
        </Typography>

        <Stack spacing={1.5}>
          {/* Topic */}
          <TextField
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Enter topic e.g. Photosynthesis"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaLightbulb size={13} color="#7c3aed" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#0c1a2e",
                borderRadius: 2,
                background: "#fff",
                "& fieldset": { borderColor: "rgba(124,58,237,0.25)" },
                "&:hover fieldset": { borderColor: "#7c3aed" },
                "&.Mui-focused fieldset": { borderColor: "#7c3aed" },
              },
              "& input::placeholder": { color: "#94a3b8", fontSize: "0.85rem" },
            }}
          />

          {/* Difficulty */}
          <Select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            displayEmpty
            size="small"
            fullWidth
            sx={{
              color: "#0c1a2e",
              borderRadius: 2,
              background: "#fff",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(124,58,237,0.25)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#7c3aed",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#7c3aed",
              },
              "& .MuiSvgIcon-root": { color: "#7c3aed" },
            }}
          >
            <MenuItem value="" disabled sx={{ color: "#94a3b8" }}>
              Select difficulty
            </MenuItem>
            {[
              {
                value: "easy",
                label: "Easy",
                color: "#059669",
                bg: "rgba(5,150,105,0.08)",
              },
              {
                value: "medium",
                label: "Medium",
                color: "#d97706",
                bg: "rgba(217,119,6,0.08)",
              },
              {
                value: "difficult",
                label: "Difficult",
                color: "#ef4444",
                bg: "rgba(239,68,68,0.08)",
              },
            ].map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: opt.color,
                    }}
                  />
                  <span>{opt.label}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>

          {/* Number of questions */}
          <TextField
            type="number"
            placeholder="Number of questions"
            value={numQuestions}
            onChange={(e) => setNumQuestions(e.target.value)}
            size="small"
            fullWidth
            inputProps={{ min: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaListUl size={13} color="#7c3aed" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#0c1a2e",
                borderRadius: 2,
                background: "#fff",
                "& fieldset": { borderColor: "rgba(124,58,237,0.25)" },
                "&:hover fieldset": { borderColor: "#7c3aed" },
                "&.Mui-focused fieldset": { borderColor: "#7c3aed" },
              },
              "& input::placeholder": { color: "#94a3b8", fontSize: "0.85rem" },
            }}
          />

          {/* Buttons */}
          <Stack direction="row" justifyContent={"space-between"} spacing={3}>
            <Button
              onClick={generateQuestions}
              disabled={loading}
              variant="contained"
              // fullWidth
              startIcon={loading ? null : <FaMagic size={12} />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.82rem",
                background: loading
                  ? "#e2e8f0"
                  : "linear-gradient(135deg, #7c3aed, #a855f7)",
                color: loading ? "#94a3b8" : "#fff",
                boxShadow: loading
                  ? "none"
                  : "0 4px 12px rgba(124,58,237,0.25)",
                "&:hover": {
                  background: loading
                    ? "#e2e8f0"
                    : "linear-gradient(135deg, #6d28d9, #7c3aed)",
                },
                "&:disabled": { color: "#94a3b8", background: "#e2e8f0" },
              }}
            >
              {loading ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={14} sx={{ color: "#94a3b8" }} />
                  <span>Generating…</span>
                </Stack>
              ) : (
                "Generate"
              )}
            </Button>

            <Button
              onClick={generateQuestions}
              disabled={loading}
              variant="outlined"
              startIcon={<FaRedo size={11} />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.82rem",
                px: 2,
                borderColor: "rgba(124,58,237,0.3)",
                color: "#7c3aed",
                "&:hover": {
                  borderColor: "#7c3aed",
                  background: "rgba(124,58,237,0.06)",
                },
              }}
            >
              Retry
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ── Results ─────────────────────────────────────────────────────────── */}
      {results.length > 0 && (
        <Box>
          <Typography
            sx={{
              fontSize: "0.68rem",
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              mb: 1.5,
            }}
          >
            Generated Questions ({results.length})
          </Typography>

          <Stack spacing={1.5} mb={2}>
            {results.map((item, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  overflow: "hidden",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "rgba(124,58,237,0.3)",
                    boxShadow: "0 2px 8px rgba(124,58,237,0.07)",
                  },
                }}
              >
                {/* Question header */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 2,
                    py: 1,
                    borderBottom: "1px solid #e2e8f0",
                    background: "#fff",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 800,
                          color: "#fff",
                        }}
                      >
                        Q{index + 1}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "#64748b",
                      }}
                    >
                      Question {index + 1}
                    </Typography>
                  </Stack>

                  <Tooltip title="Remove question" arrow>
                    <IconButton
                      size="small"
                      onClick={() => removeQuestion(index)}
                      sx={{
                        color: "#ef4444",
                        background: "rgba(239,68,68,0.08)",
                        border: "1px solid rgba(239,68,68,0.2)",
                        borderRadius: 1.5,
                        "&:hover": { background: "rgba(239,68,68,0.16)" },
                      }}
                    >
                      <FaTrash size={12} />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {/* Question body */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    fontSize: "0.88rem",
                    color: "#0c1a2e",
                    lineHeight: 1.7,
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {renderContent(item)}
                </Box>
              </Paper>
            ))}
          </Stack>

          {/* Save button */}
          <Button
            onClick={saveQuestions}
            disabled={saving}
            variant="contained"
            fullWidth
            startIcon={saving ? null : <FaCheck size={12} />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.88rem",
              py: 1.2,
              background: saving
                ? "#e2e8f0"
                : "linear-gradient(135deg, #059669, #10b981)",
              color: saving ? "#94a3b8" : "#fff",
              boxShadow: saving ? "none" : "0 4px 12px rgba(5,150,105,0.25)",
              "&:hover": {
                background: saving
                  ? "#e2e8f0"
                  : "linear-gradient(135deg, #047857, #059669)",
              },
              "&:disabled": { color: "#94a3b8", background: "#e2e8f0" },
            }}
          >
            {saving ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} sx={{ color: "#94a3b8" }} />
                <span>Saving…</span>
              </Stack>
            ) : (
              `Save All ${results.length} Questions`
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CreateObjectiveQuestion;